import {
  GiftCardsGiftCardData,
  ModifyQuantityOrderedResponse,
  SvcAddDigitalGiftCardToOrder,
  SvcAddDiscountCouponToOrder,
  SvcAddProductToOrder,
  SvcCancelDiscountOrderLine,
  SvcCancelOrder,
  SvcChangeOrderLinesToDelivery,
  SvcChangeOrderLinesToPickup,
  SvcGetGiftWrappingOptionsForOrder,
  SvcGetProductAvailability,
  SvcGetProductRequirementValuesForOrderLine,
  SvcGetShoppingCart,
  SvcModifyQuantityOrdered,
  SvcPlaceOrder,
  SvcPrepareOrderForCheckout,
  SvcSetGiftWrappingOptionsOnOrder,
  SvcSetPickProductDiscountOptionsForOrderLine,
  SvcUpdateOrderAddresses,
  SvcUpdateOrderBillingAddress,
  SvcUpdateOrderCustomFields,
  SvcUpdateOrderShippingAddress,
  SvcUpdateProductRequirementValuesForOrderLine,
} from "@springtree/eva-services-core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { HTTPError } from "ky";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";

import { isLoadingCartAtom, isPendingCartAtom, shoppingCartAtom } from "~/atoms/cart";
import { ProductActionOutcome } from "~/components/common/product-actions";
import { GiftCard } from "~/components/product/product-gift-addtobag";
import { useAuthContext } from "~/contexts/auth";
import { useBreakpoint } from "~/contexts/breakpoint";
import { useCartNotification } from "~/contexts/cart-notification";
import { useConfiguration } from "~/contexts/configuration";
import { useEvaCartAdd, useEvaCartModify } from "~/hooks/use-eva-cart";
import { useEvaService } from "~/hooks/use-eva-service";
import { useGuestCartStorage } from "~/hooks/use-guest-cart-storage";
import { algoliaAddToBag } from "~/services/algolia-utils";
import Logger from "~/services/logger/logger";
import { StorageFactory } from "~/services/storage.factory";
import { CacheKeys } from "~/shared/cache-keys";
import { ChangeQuantityDesktopProps, ChangeQuantityMobileProps } from "~/shared/product/quantity-selector-fields";
import { ListProductTrackingInfo } from "~/types/trackings.models";
import appRoutes from "~/utils/app-routes";
import productUtils from "~/utils/product-utils";
import TrackingUtils from "~/utils/tracking-utils";

import { useGlobalNotification } from "./use-global-notification";
import { useLinesProductsHierarchicalData } from "./use-lines-container";

type couponErrorResponse = {
  Code: string;
  Message: string;
  RequestID: string;
  Type: string;
};

function _trackCartEcomAction(action: ProductActionOutcome, quantity: number, trackingData?: ListProductTrackingInfo) {
  if (trackingData) {
    if (trackingData.info) {
      trackingData.info.quantity = quantity;
    }

    const trackPayload = TrackingUtils.mapBasketAction("cart", action, trackingData.context ?? "", trackingData.info);
    trackPayload && TrackingUtils.track(trackPayload);
  }
}

export function useCart() {
  const cart = useAtomValue(shoppingCartAtom);
  return cart;
}

export function useCartProductsHierarchicalData() {
  const cart = useCart();
  return useLinesProductsHierarchicalData(cart?.ShoppingCart?.Lines || []);
}

// This hook is used to check the availability of products in the cart.
export function useCartAvailability() {
  const cart = useCart();
  const cartId = cart?.ShoppingCart?.ID;

  const getProductAvailability = useEvaService(SvcGetProductAvailability);

  const lines = cart?.ShoppingCart?.Lines?.filter((line) => line.Product) ?? [];

  let cartProducts: typeof lines = [];
  let cartOutOfAssortmentProducts: string[] = [];

  for (const line of lines) {
    const product = line.Product;
    if (product) {
      cartProducts.push(line);
      if (!product?.Properties && line.ProductID) {
        cartOutOfAssortmentProducts.push(String(line.ProductID));
      }
    }
  }

  let cartProductIdsAndQuantity = cartProducts.map((p) => [p.ProductID, p.QuantityOpen]) ?? [];

  // Filter the product IDs that are either type 8 or digital gift cards
  let excludedProductIds = cartProducts
    .filter((p) => productUtils.isGiftCard(p.Product?.Properties) || p.IsDigitalGiftCard)
    .map((p) => p.ProductID!);

  // Get the products availability
  const { data: cartAvailabilityData } = useQuery(
    ["getProductAvailability", cartId, ...cartProductIdsAndQuantity],
    async () => {
      if (cartProducts.length > 0) {
        const productAvailability = await getProductAvailability({
          Products: cartProducts?.map((line) => ({
            ID: line.ProductID,
            QuantityRequested: line.QuantityOpen,
          })),
          Options: {
            Pickup: {
              IncludePickupOrganizationUnits: true,
              IncludeOrganizationUnitsWithoutStock: true,
            },
            Delivery: {
              AvailabilityDate: true,
            },
          },
        });
        if (cartOutOfAssortmentProducts.length > 0) {
          productAvailability?.Products?.map((product) => {
            if (product.ProductID && cartOutOfAssortmentProducts.find((p) => p === String(product.ProductID))) {
              if (product.Delivery) {
                product.Delivery.HasStock = false;
                product.Delivery.IsAvailable = false;
              }
              if (product.Pickup) {
                product.Pickup.HasStock = false;
                product.Pickup.IsAvailable = false;
              }
            }

            return product;
          });
        }

        return productAvailability;
      }
      return null;
    },
    {
      keepPreviousData: true,
      enabled: !!cartId && cartProducts.length > 0,
      staleTime: 10 * 1000,
      refetchOnMount: true, // Refetch on mount to handle existing carts after deployment
    }
  );

  // Return the cart availability data if it exists
  return cartAvailabilityData
    ? {
        ...cartAvailabilityData,
        AllAvailable:
          cartOutOfAssortmentProducts.length === 0 &&
          cartAvailabilityData?.Products?.every(
            (p) => p.Delivery?.HasStock || excludedProductIds.includes(p.ProductID!)
          ),
      }
    : undefined;
}

export function useCartWithPendingStatus() {
  const cart = useAtomValue(shoppingCartAtom);
  const isLoadingCart = useAtomValue(isLoadingCartAtom);
  return { cart, isLoadingCart };
}

export function useCartWithHooks() {
  const cart = useCart();
  const [couponError, setCouponError] = useState<couponErrorResponse>();

  const setPickProduct = useEvaCartModify(SvcSetPickProductDiscountOptionsForOrderLine);
  const addCouponCode = useEvaCartAdd(SvcAddDiscountCouponToOrder, {
    onError: async (error) => {
      const response = (error as HTTPError)?.response;
      if (response) {
        const jsonError = (await response.json()) as any;
        setCouponError(jsonError.Error);
      }
    },
  });
  const cancelDiscountOrderLine = useEvaCartModify(SvcCancelDiscountOrderLine);

  return { cart, addCouponCode, setPickProduct, cancelDiscountOrderLine, couponError, setCouponError };
}

export function useCartProduct(productId: string, lineID?: string, trackingData?: ListProductTrackingInfo) {
  const cart = useCart();
  const router = useRouter();
  const { algoliaIndexId } = useConfiguration();
  const { notifyAdded, notifyRemoved, notifyModified, notifyFixed, notifyOOS, resetNotifications } =
    useCartNotification();
  const { newErrorGlobalNotification, closeGlobalNotification } = useGlobalNotification();
  const { logout, isAuthenticated } = useAuthContext();

  // State to manage the logout->add-to-cart sequence
  const [isGuestCheckoutRestarting, setGuestCheckoutRestarting] = useState(false);

  const cartErrorGlobalNotification = () => {
    newErrorGlobalNotification({
      slug: "cartGenericError",
      closeOnClickCta: true,
      ctaCaption: "generic.cancel",
    });
  };

  const line = useMemo(() => {
    if (lineID) {
      return cart?.ShoppingCart?.Lines?.find((line) => line.ID === lineID) ?? null;
    }

    // NOTE: string conversion is needed by ECO-311 for string types
    return cart?.ShoppingCart?.Lines?.find((line) => `${line.ProductID}` === `${productId}`) ?? null;
  }, [cart?.ShoppingCart?.Lines, lineID, productId]);

  const isCartPaid = !!cart?.ShoppingCart?.IsPaid;

  const getShoppingCart = useEvaService(SvcGetShoppingCart);
  const shoppingCartAdd = useEvaCartAdd(SvcAddProductToOrder);
  const addDigitalCard = useEvaCartAdd(SvcAddDigitalGiftCardToOrder);
  const shoppingCartModify = useEvaCartModify(SvcModifyQuantityOrdered);
  const shoppingCartCancel = useEvaCartModify(SvcCancelOrder);
  const getProductRequiremens = useEvaService(SvcGetProductRequirementValuesForOrderLine);
  const getProductAvailability = useEvaService(SvcGetProductAvailability);
  const updateProductRequirements = useEvaCartAdd(SvcUpdateProductRequirementValuesForOrderLine);
  // Use localStorage for guest cart flags to align with EVA cookie persistence
  const cache = useGuestCartStorage();
  const queryClient = useQueryClient();
  const refreshCart = () => queryClient.invalidateQueries({ queryKey: ["shoppingCart"] });
  const addGiftCard = useMutation(
    async (giftParams: GiftCard) => {
      try {
        const addedAt = new Date();

        if (giftParams.isVirtual) {
          const additionalData = {
            FromEmailAddress: cart?.ShoppingCart?.Customer?.EmailAddress || undefined,
            ToEmailAddress: giftParams.recipient,
            Theme: {
              ImageUrl: giftParams.imageUrl,
              Name: giftParams.cardName,
            },
          } as GiftCardsGiftCardData;

          // if gift card, add custom fields
          if (giftParams.isGift) {
            additionalData.From = giftParams.senderName;
            additionalData.To = giftParams.recipientName;
            additionalData.Text = giftParams.message;
          }
          await addDigitalCard({
            orderType: 0,
            createNewOrderWhen: "paid",
            payload: {
              ProductID: productId,
              Amount: giftParams.amount || giftParams.customAmount,
              Data: additionalData,
            },
          }).then((data) => {
            if (!!data) {
              notifyAdded(addedAt);
            } else {
              throw new Error("addGiftCard > addDigitalCard errored");
            }
          });
        } else {
          const shoppingCartAddResponse = await shoppingCartAdd({
            orderType: 0,
            createNewOrderWhen: "paid",
            payload: {
              ProductID: productId,
              QuantityOrdered: 1,
              UnitPriceInTax: giftParams.amount || giftParams.customAmount,
            },
          });

          if (!shoppingCartAddResponse) {
            throw new Error("addGiftCard > shoppingCartAdd errored");
          }

          if (!!shoppingCartAddResponse) {
            if (giftParams.isGift) {
              const productRequirementsResponse = await getProductRequiremens({
                OrderLineID: `${shoppingCartAddResponse.OrderLineID}`,
              });

              if (!productRequirementsResponse) {
                throw new Error("addGiftCard > getProductRequiremens errored");
              }

              if (productRequirementsResponse?.Requirements) {
                // assuming SenderName and RecipientName will not change in position between org units, otherwise we need to find another way to handle this
                const requiremensIds = Object.keys(productRequirementsResponse.Requirements);
                await updateProductRequirements({
                  orderType: 0,
                  payload: {
                    OrderLineID: shoppingCartAddResponse.OrderLineID,
                    Values: {
                      [requiremensIds[0]]: giftParams.senderName,
                      [requiremensIds[1]]: giftParams.recipientName,
                    },
                  },
                }).then((data) => {
                  if (!data) throw new Error("addGiftCard > updateProductRequirements errored");
                });
                // TEMP FIX with setTimeout
                setTimeout(() => {}, 100);
              }
            }
            notifyAdded(addedAt);
          }
        }
      } catch (err) {
        Logger.instance.error("Usecart > addGiftCard errored", err);
        throw err;
      }
    },
    {
      onSuccess: () => {
        closeGlobalNotification();
        queryClient.invalidateQueries(["digitalGiftCardsDetails", cart?.ShoppingCart?.ID]);
      },
      onError: () => {
        resetNotifications();
        cartErrorGlobalNotification();
      },
    }
  );

  /**
   * This handles the case where a guest user places 2 orders sequentially.
   * The problem is that the same user ends up having multiple orders associated, which goes against the principle of anonimity.
   * So we force the logout if in this session I reached the TYP, and I'm adding a product to the cart.
   */
  useEffect(() => {
    if (!isGuestCheckoutRestarting) {
      return;
    }

    const restartAddToCart = async () => {
      try {
        const addedAt = new Date();
        await shoppingCartAdd({
          orderType: 0,
          createNewOrderWhen: "paid",
          payload: { ProductID: productId, QuantityOrdered: 1 },
          callOptions: {
            authenticationToken: "",
          },
        }).then((data) => {
          if (!data) throw new Error("addToCart > shoppingCartAdd errored");
        });
        _trackCartEcomAction("added", 1, trackingData);
        notifyAdded(addedAt);
        closeGlobalNotification();
      } catch (err) {
        Logger.instance.error("Usecart > addToCart (restarted) errored", err);
      }
      // Reset the state machine
      setGuestCheckoutRestarting(!1);
    };

    restartAddToCart();
  }, [closeGlobalNotification, isGuestCheckoutRestarting, notifyAdded, productId, shoppingCartAdd, trackingData]);

  const addToCart = useMutation({
    mutationFn: async () => {
      try {
        const addedAt = new Date();

        if (line?.QuantityOpen) {
          await shoppingCartModify({
            payload: { Lines: [{ OrderLineID: line.ID, NewQuantityOrdered: line.QuantityOpen + 1 }] },
          }).then((data) => {
            if (!data) throw new Error("addToCart > shoppingCartModify errored");
          });
          notifyAdded(addedAt);
        } else if (cache.get(CacheKeys.ANONYMOUS_ORDER_COMPLETED)) {
          if (!isAuthenticated) {
            await logout();
            cache.remove(CacheKeys.ANONYMOUS_ORDER_COMPLETED);
            setGuestCheckoutRestarting(true);
          } else {
            // For logged users, just clear the flag and create new cart
            cache.remove(CacheKeys.ANONYMOUS_ORDER_COMPLETED);
            await shoppingCartAdd({
              orderType: 0,
              createNewOrderWhen: "paid",
              payload: { ProductID: productId, QuantityOrdered: 1 },
            }).then((data) => {
              if (!data) throw new Error("addToCart > shoppingCartAdd errored");
            });
            _trackCartEcomAction("added", 1, trackingData);
            notifyAdded(addedAt);
          }
        } else {
          await shoppingCartAdd({
            orderType: 0,
            createNewOrderWhen: "paid",
            payload: { ProductID: productId, QuantityOrdered: 1 },
          }).then((data) => {
            if (!data) throw new Error("addToCart > shoppingCartAdd errored");
          });
          _trackCartEcomAction("added", 1, trackingData);
          notifyAdded(addedAt);
        }
      } catch (err) {
        Logger.instance.error("Usecart > addToCart errored", err);
        throw err;
      }
    },
    onSuccess: () => {
      closeGlobalNotification();
    },
    onError: () => {
      resetNotifications();
      cartErrorGlobalNotification();
    },
  });

  //TODO: removeFromCart unused
  const removeFromCart = useMutation({
    mutationFn: async () => {
      const removedAt = new Date();

      if (!line) {
        return;
      }

      if (line.QuantityOpen !== undefined) {
        if (line.QuantityOpen === 1) {
          await shoppingCartCancel({
            payload: { OrderID: line.OrderID, OrderLines: [{ OrderLineID: line.ID }] },
          });
        } else {
          await shoppingCartModify({
            payload: { Lines: [{ OrderLineID: line.ID, NewQuantityOrdered: line.QuantityOpen - 1 }] },
          });
        }
      }

      _trackCartEcomAction("removed", 1, trackingData);

      notifyRemoved(removedAt);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["digitalGiftCardsDetails", cart?.ShoppingCart?.ID]);
    },
  });

  // TODO: maybe not the best solution to pass max quantity to the hook
  const changeQuantity = useMutation({
    mutationFn: async (
      data: ReturnType<typeof useBreakpoint> extends "desktop" ? ChangeQuantityDesktopProps : ChangeQuantityMobileProps
    ) => {
      try {
        let newMaxQuantity: number | undefined = undefined;
        const modifiedAt = new Date();

        const checkQuantityExceedsMaximum = async (data?: ModifyQuantityOrderedResponse) => {
          const messages = data?.Result?.[0]?.Messages ?? null;
          if (!messages) return;

          const hasQuantityExceedsMaximum = messages.some((msg) => msg.Code === "QuantityExceedsMaximum");

          if (hasQuantityExceedsMaximum) {
            if (!line?.OrderID) return;
            const cart = await getShoppingCart({ OrderID: line.OrderID });
            if (cart) {
              const newLine = cart.ShoppingCart?.Lines?.find((l) => line?.ID === l.ID) ?? null;
              newMaxQuantity = newLine?.QuantityOpen;
            } else {
              throw new Error("QuantityExceedsMaximum > getShoppingCart errored");
            }
          }
        };

        if (!line) {
          return;
        }

        if (line.QuantityOpen === data.Quantity) {
          return;
        }
        if (router.pathname !== appRoutes.CART) {
          const productAvailability = await getProductAvailability({
            Products: [{ ID: line.ProductID, QuantityRequested: data.Quantity }],
            Options: {
              Pickup: {
                IncludePickupOrganizationUnits: true,
                IncludeOrganizationUnitsWithoutStock: true,
              },
              Delivery: {
                AvailabilityDate: true,
              },
            },
          });

          if (productAvailability?.Products && !productAvailability?.Products[0]?.Delivery?.HasStock) {
            await shoppingCartModify({
              payload: { Lines: [{ OrderLineID: line.ID, NewQuantityOrdered: 1 }] },
            }).then((data) => {
              if (!data) throw new Error("changeQuantity product OOS > shoppingCartModify errored");
            });

            notifyOOS(modifiedAt);
            return { isOOS: true };
          }
        }

        const diffQuantity = data.Quantity - (line.QuantityOpen || 0);

        if (diffQuantity < 0) {
          // removal of items
          if (data.Quantity <= 0) {
            await shoppingCartCancel({
              payload: { OrderID: line.OrderID, OrderLines: [{ OrderLineID: line.ID }] },
            }).then((data) => {
              if (!data) throw new Error("changeQuantity > shoppingCartCancel errored");
            });
            notifyRemoved(modifiedAt);
          } else {
            await shoppingCartModify({
              payload: { Lines: [{ OrderLineID: line.ID, NewQuantityOrdered: data.Quantity }] },
            }).then((data) => {
              if (!data)
                throw new Error("changeQuantity (diffQuantity < 0 && data.Quantity > 0) > shoppingCartModify errored");
            });
            notifyModified(modifiedAt);
          }
          _trackCartEcomAction("removed", -diffQuantity, trackingData);
        } else if (diffQuantity > 0) {
          // adding of items
          if (line?.QuantityOpen) {
            if (data.Quantity === 1) {
              await shoppingCartAdd({
                orderType: 0,
                createNewOrderWhen: "paid",
                payload: { ProductID: productId, QuantityOrdered: 1 },
              }).then((data) => {
                if (!data)
                  throw new Error("changeQuantity (diffQuantity > 0 && data.Quantity === 1) > shoppingCartAdd errored");
              });
              notifyAdded(modifiedAt);
            } else if (data.Quantity > data.maxQuantity) {
              await shoppingCartModify({
                payload: { Lines: [{ OrderLineID: line.ID, NewQuantityOrdered: data.maxQuantity }] },
              }).then(async (data) => {
                await checkQuantityExceedsMaximum(data);
                if (!data)
                  throw new Error(
                    "changeQuantity > shoppingCartModify (diffQuantity > 0 && data.Quantity > data.maxQuantity) errored"
                  );
              });
              notifyFixed(modifiedAt);
            } else {
              await shoppingCartModify({
                payload: { Lines: [{ OrderLineID: line.ID, NewQuantityOrdered: data.Quantity }] },
              }).then(async (data) => {
                await checkQuantityExceedsMaximum(data);
                if (!data) throw new Error("changeQuantity > shoppingCartModify errored");
              });
              notifyModified(modifiedAt);
            }
            _trackCartEcomAction("added", diffQuantity, trackingData);
            algoliaAddToBag(algoliaIndexId, productId, trackingData?.info, diffQuantity);
          }
        }

        //When the "QuantityExceedsMaximum" error message occurs, the maximum quantity is updated.
        if (newMaxQuantity) {
          notifyFixed(modifiedAt);
          return { newMaxQuantity };
        }
      } catch (err) {
        Logger.instance.error("Usecart > changeQuantity errored", err);
        throw err;
      }
    },
    onSuccess: () => {
      closeGlobalNotification();
    },
    onError: () => {
      resetNotifications();
      cartErrorGlobalNotification();
    },
  });

  const removeAllFromCart = useMutation({
    mutationFn: async () => {
      if (!line) {
        return;
      }

      const diffQuantity = line?.QuantityOpen || 0;
      await shoppingCartCancel({
        payload: { OrderID: line.OrderID, OrderLines: [{ OrderLineID: line.ID }] },
      });
      _trackCartEcomAction("removed", diffQuantity, trackingData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["digitalGiftCardsDetails", cart?.ShoppingCart?.ID]);
    },
  });

  return {
    line,
    quantityInCart: line?.QuantityOpen ?? 0,
    addToCart,
    addGiftCard,
    changeQuantity,
    removeFromCart,
    removeAllFromCart,
    isCartPaid,
  };
}

export function useCartCheckout() {
  const cart = useCart();
  const cartAvailability = useCartAvailability();

  const getShoppingCart = useEvaService(SvcGetShoppingCart);
  const changeOrderLinesToDelivery = useEvaCartModify(SvcChangeOrderLinesToDelivery);
  const changeOrderLinesToPickup = useEvaCartModify(SvcChangeOrderLinesToPickup);
  const updateOrderShippingAddress = useEvaCartModify(SvcUpdateOrderShippingAddress);
  const updateOrderBillingAddress = useEvaCartModify(SvcUpdateOrderBillingAddress);
  const updateOrderAddresses = useEvaCartModify(SvcUpdateOrderAddresses);
  const prepareOrderForCheckout = useEvaCartModify(SvcPrepareOrderForCheckout);
  const placeOrder = useEvaCartModify(SvcPlaceOrder);
  const getGiftWrappingOptionsForOrder = useEvaCartModify(SvcGetGiftWrappingOptionsForOrder);
  const setGiftWrappingOptionsOnOrder = useEvaCartModify(SvcSetGiftWrappingOptionsOnOrder);
  const updateOrderCustomFields = useEvaCartModify(SvcUpdateOrderCustomFields);

  return {
    cart,
    cartAvailability,
    getShoppingCart,
    changeOrderLinesToDelivery,
    changeOrderLinesToPickup,
    updateOrderShippingAddress,
    updateOrderBillingAddress,
    updateOrderAddresses,
    prepareOrderForCheckout,
    placeOrder,
    setGiftWrappingOptionsOnOrder,
    getGiftWrappingOptionsForOrder,
    updateOrderCustomFields,
  };
}

export function useCartProductsCache() {
  const cart = useCart();
  const isPendingCart = useAtomValue(isPendingCartAtom);
  const [cartProductsNumber, setCartProductsNumber] = useState<number>();
  const cache = useMemo(() => new StorageFactory<CacheKeys>("sessionStorage"), []);
  const showBlank = isPendingCart || cartProductsNumber === undefined;

  useEffect(() => {
    const cachedNumber = cache.get(CacheKeys.CART_PRODUCTS_NUMBER);
    const cachedProductNumber = cachedNumber ? parseInt(cachedNumber) : 0;
    setCartProductsNumber(cachedProductNumber);
  }, [cache]);

  return { cart, isPendingCart, showBlank, cartProductsNumber };
}
